/**
 * ExploreWorkbookModal Store
 *
 * Manages state for the workbook explorer modal: the workbook/folder being browsed, search
 * params, pagination, sort, loading state, etc. Used when picking an analysis/workbook (e.g.
 * cloning a worksheet, inserting report content, selecting items from a worksheet).
 *
 * `pageSize` and `initialFolderId` are persisted at the WORKSHEET level; the remaining fields
 * are transient and reset when the modal clears.
 */

import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { SEARCH_ITEM_LOCATIONS } from '@/main/app.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import type { Folder, Item, SearchParams } from '@/explorer/exploreModal.types';
import type { SearchItemLocation } from '@/main/app.constants';
import type { RootEnum } from '@seeqdev/api/endpoint/FoldersApi';

export interface ExploreWorkbookModalState {
  /** ID of the folder the user landed on when the modal opened. */
  initialFolderId: string | undefined;
  /** ID of the workbook the user selected, or `undefined` if none. */
  workbookId: string | undefined;
  /** Type of the selected workbook (e.g., Analysis, Vantage). */
  workbookType: string | undefined;
  /** Active location filter for the table (Recent, My Folder, Corporate, Shared, etc.). */
  tableFilter: SearchItemLocation;
  /** Total number of items matching the current query (for pagination). */
  itemTotalForTable: number;
  /** Page size for the table. Persisted. */
  pageSize: number;
  /** 1-based current page number. */
  pageNumber: number;
  /** Active search parameters sent to the folders API. */
  searchParams: Partial<SearchParams>;
  /** Items currently displayed in the table. */
  items: Item[];
  /** True while items are being loaded. */
  loading: boolean;
  /** Folder currently focused in the explorer. */
  folder: Folder | undefined;
  /** True when sort should be ascending. */
  sortAscending: boolean;
  /** Field the table is currently sorted by. */
  sortProperty: string;
  /** True when the search results header should be displayed. */
  showResults: boolean;
  /** Root scope used for restricting the folders query (e.g., USERS). */
  tableRoot: RootEnum | undefined;
}

/** Subset of state persisted at the worksheet level. */
export type ExploreWorkbookModalDehydratedState = Pick<ExploreWorkbookModalState, 'pageSize' | 'initialFolderId'>;

const initialState: ExploreWorkbookModalState = Object.freeze({
  initialFolderId: undefined,
  workbookId: undefined,
  workbookType: undefined,
  tableFilter: SEARCH_ITEM_LOCATIONS.RECENT,
  itemTotalForTable: 0,
  pageSize: 5,
  pageNumber: 1,
  searchParams: { types: ['Folder', 'Analysis'] },
  items: [],
  loading: false,
  folder: undefined,
  sortAscending: false,
  sortProperty: 'openedAt',
  showResults: false,
  tableRoot: undefined,
});

export const dehydrateExploreWorkbookModalState = (
  state: ExploreWorkbookModalState,
): ExploreWorkbookModalDehydratedState => ({
  pageSize: state.pageSize,
  initialFolderId: state.initialFolderId,
});

export interface ExploreWorkbookModalActions {
  setWorkbookId: (workbookId: string | undefined, workbookType: string | undefined) => void;
  setInitialFolderId: (initialFolderId: string | undefined) => void;
  setTableFilter: (tableFilter: SearchItemLocation) => void;
  setItemTotalForTable: (itemTotalForTable: number) => void;
  setPageSize: (pageSize: number) => void;
  setPageNumber: (pageNumber: number) => void;
  setItems: (items: Item[]) => void;
  /**
   * Updates a single search parameter field. Passing `''` removes the field entirely;
   * any other value (including `undefined`) is assigned to the field.
   */
  setSearchParam: (field: keyof SearchParams, value: unknown) => void;
  setLoading: (loading: boolean) => void;
  setFolder: (folder: Folder | undefined) => void;
  setSort: (payload: { sortProperty: string; sortAscending: boolean }) => void;
  setShowResults: (showResults: boolean) => void;
  setTableRoot: (root: RootEnum | undefined) => void;
  /**
   * Clears the transient selection state (initial folder, selected workbook, table root)
   * while preserving the rest of the in-session table state. Mirrors the legacy
   * `EXPLORE_WORKBOOK_MODAL_CLEAR` reinitialization behavior.
   */
  clear: () => void;
  reset: () => void;
}

const exploreWorkbookModalActionsFactory: ActionsFactory<ExploreWorkbookModalState, ExploreWorkbookModalActions> = (
  set,
  _get,
  resetState,
) => ({
  setInitialFolderId: (initialFolderId) =>
    set((draft) => {
      draft.initialFolderId = initialFolderId;
    }),
  setWorkbookId: (workbookId, workbookType) =>
    set((draft) => {
      draft.workbookId = workbookId;
      draft.workbookType = workbookType;
    }),

  setTableFilter: (tableFilter) =>
    set((draft) => {
      draft.tableFilter = tableFilter;
    }),

  setItemTotalForTable: (itemTotalForTable) =>
    set((draft) => {
      draft.itemTotalForTable = itemTotalForTable;
    }),

  setPageSize: (pageSize) =>
    set((draft) => {
      draft.pageSize = pageSize;
    }),

  setPageNumber: (pageNumber) =>
    set((draft) => {
      draft.pageNumber = pageNumber;
    }),

  setItems: (items) =>
    set((draft) => {
      draft.items = items;
    }),

  setSearchParam: (field, value) =>
    set((draft) => {
      if (value === '') {
        delete (draft.searchParams as Record<string, unknown>)[field];
      } else {
        (draft.searchParams as Record<string, unknown>)[field] = value;
      }
    }),

  setLoading: (loading) =>
    set((draft) => {
      draft.loading = loading;
    }),

  setFolder: (folder) =>
    set((draft) => {
      draft.folder = folder;
    }),

  setSort: (payload) =>
    set((draft) => {
      draft.sortProperty = payload.sortProperty;
      draft.sortAscending = payload.sortAscending;
    }),

  setShowResults: (showResults) =>
    set((draft) => {
      draft.showResults = showResults;
    }),

  setTableRoot: (root) =>
    set((draft) => {
      draft.tableRoot = root;
    }),

  clear: () =>
    set((draft) => {
      draft.initialFolderId = undefined;
      draft.workbookId = undefined;
      draft.workbookType = undefined;
      draft.tableRoot = undefined;
    }),

  reset: resetState,
});

const STORE_NAME = 'ExploreWorkbookModal';

const [ExploreWorkbookModalStoreProvider, useExploreWorkbookModalStoreInstance, useExploreWorkbookModalSelector] =
  createStore(STORE_NAME, initialState, exploreWorkbookModalActionsFactory);

export { ExploreWorkbookModalStoreProvider };

// Selector hooks
export const useExploreWorkbookModalInitialFolderId = () =>
  useExploreWorkbookModalSelector((state) => state.initialFolderId);

export const useExploreWorkbookModalWorkbookId = () => useExploreWorkbookModalSelector((state) => state.workbookId);

export const useExploreWorkbookModalWorkbookType = () => useExploreWorkbookModalSelector((state) => state.workbookType);

export const useExploreWorkbookModalTableFilter = () => useExploreWorkbookModalSelector((state) => state.tableFilter);

export const useExploreWorkbookModalItemTotalForTable = () =>
  useExploreWorkbookModalSelector((state) => state.itemTotalForTable);

export const useExploreWorkbookModalPageSize = () => useExploreWorkbookModalSelector((state) => state.pageSize);

export const useExploreWorkbookModalPageNumber = () => useExploreWorkbookModalSelector((state) => state.pageNumber);

export const useExploreWorkbookModalSearchParams = () => useExploreWorkbookModalSelector((state) => state.searchParams);

export const useExploreWorkbookModalItems = () => useExploreWorkbookModalSelector((state) => state.items);

export const useExploreWorkbookModalLoading = () => useExploreWorkbookModalSelector((state) => state.loading);

export const useExploreWorkbookModalFolder = () => useExploreWorkbookModalSelector((state) => state.folder);

export const useExploreWorkbookModalSortAscending = () =>
  useExploreWorkbookModalSelector((state) => state.sortAscending);

export const useExploreWorkbookModalSortProperty = () => useExploreWorkbookModalSelector((state) => state.sortProperty);

export const useExploreWorkbookModalShowResults = () => useExploreWorkbookModalSelector((state) => state.showResults);

export const useExploreWorkbookModalTableRoot = () => useExploreWorkbookModalSelector((state) => state.tableRoot);

/** Returns the textSearch value from searchParams, or undefined. */
const selectSearchText = createSelector(
  (state: ExploreWorkbookModalState) => state.searchParams,
  (searchParams) => searchParams.textSearch,
);
export const useExploreWorkbookModalSearchText = () => useExploreWorkbookModalSelector(selectSearchText);

export const useExploreWorkbookModalActions = () => useExploreWorkbookModalStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateExploreWorkbookModalStore(initialStateOverrides?: Partial<ExploreWorkbookModalState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    exploreWorkbookModalActionsFactory,
    { storeName: STORE_NAME },
  );
}

export type ExploreWorkbookModalStore = ReturnType<typeof legacyCreateExploreWorkbookModalStore>;

/**
 * Registers the ExploreWorkbookModal store with the persistence pipeline so the
 * persisted subset survives across worksheet loads.
 */
export const usePersistExploreWorkbookModalStore = () => {
  const register = useRegisterPersistedStore();
  const store = useExploreWorkbookModalStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqExploreWorkbookModalStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateExploreWorkbookModalState,
    });
  }, [register, store]);
};
